import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, Plus, Play, Pause, Star, Trash2, 
  Edit2, Download, Share2, Heart, Clock, Calendar,
  Filter, Search, Volume2, Archive, Shield
} from 'lucide-react';

interface Recording {
  id: string;
  prompt: string;
  response: string;
  audioUrl?: string;
  createdAt: string | Date;
  rating?: number;
  category?: string;
  duration?: number;
  isFavorite?: boolean;
  tags?: string[];
}

export const RecordingsLibrary: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [filteredRecordings, setFilteredRecordings] = useState<Recording[]>([]);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});

  useEffect(() => {
    loadRecordings();
  }, []);

  useEffect(() => {
    filterRecordings();
  }, [recordings, searchQuery, filterCategory]);

  const loadRecordings = () => {
    const stored = localStorage.getItem('recordings');
    if (stored) {
      const parsed = JSON.parse(stored);
      setRecordings(parsed);
    }
  };

  const filterRecordings = () => {
    let filtered = [...recordings];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(r => 
        r.prompt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.response.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      if (filterCategory === 'favorites') {
        filtered = filtered.filter(r => r.isFavorite);
      } else if (filterCategory === 'recent') {
        // Show last 7 days
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        filtered = filtered.filter(r => new Date(r.createdAt) > weekAgo);
      }
    }

    setFilteredRecordings(filtered);
  };

  const togglePlayback = (recording: Recording) => {
    if (playingId === recording.id) {
      // Stop playing
      if (audioRefs.current[recording.id]) {
        audioRefs.current[recording.id].pause();
      } else {
        speechSynthesis.cancel();
      }
      setPlayingId(null);
    } else {
      // Stop any currently playing
      if (playingId) {
        if (audioRefs.current[playingId]) {
          audioRefs.current[playingId].pause();
        }
        speechSynthesis.cancel();
      }

      // Start playing new
      setPlayingId(recording.id);
      
      if (recording.audioUrl) {
        if (!audioRefs.current[recording.id]) {
          audioRefs.current[recording.id] = new Audio(recording.audioUrl);
          audioRefs.current[recording.id].onended = () => setPlayingId(null);
        }
        audioRefs.current[recording.id].play();
      } else {
        // Use browser TTS
        const utterance = new SpeechSynthesisUtterance(recording.response);
        utterance.rate = 0.9;
        utterance.onend = () => setPlayingId(null);
        speechSynthesis.speak(utterance);
      }
    }
  };

  const updateRating = (id: string, rating: number) => {
    const updated = recordings.map(r => 
      r.id === id ? { ...r, rating } : r
    );
    setRecordings(updated);
    localStorage.setItem('recordings', JSON.stringify(updated));
  };

  const toggleFavorite = (id: string) => {
    const updated = recordings.map(r => 
      r.id === id ? { ...r, isFavorite: !r.isFavorite } : r
    );
    setRecordings(updated);
    localStorage.setItem('recordings', JSON.stringify(updated));
  };

  const deleteRecording = (id: string) => {
    if (confirm('Delete this recording?')) {
      const updated = recordings.filter(r => r.id !== id);
      setRecordings(updated);
      localStorage.setItem('recordings', JSON.stringify(updated));
    }
  };

  const startEditing = (recording: Recording) => {
    setEditingId(recording.id);
    setEditingText(recording.response);
  };

  const saveEdit = (id: string) => {
    const updated = recordings.map(r => 
      r.id === id ? { ...r, response: editingText } : r
    );
    setRecordings(updated);
    localStorage.setItem('recordings', JSON.stringify(updated));
    setEditingId(null);
    
    // Regenerate audio if needed
    regenerateAudio(id, editingText);
  };

  const regenerateAudio = async (id: string, text: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/audio/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'rachel' })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_url) {
          const updated = recordings.map(r => 
            r.id === id ? { ...r, audioUrl: data.audio_url } : r
          );
          setRecordings(updated);
          localStorage.setItem('recordings', JSON.stringify(updated));
        }
      }
    } catch (error) {
      console.log('Could not regenerate audio');
    }
  };

  const shareRecording = async (recording: Recording) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Selfi Message',
          text: recording.response,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Copy to clipboard
      navigator.clipboard.writeText(recording.response);
      alert('Copied to clipboard!');
    }
  };

  const downloadRecording = (recording: Recording) => {
    const content = `Prompt: ${recording.prompt}\n\nResponse: ${recording.response}\n\nDate: ${new Date(recording.createdAt).toLocaleString()}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selfi-${recording.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const exportAllRecordings = () => {
    const data = {
      exportDate: new Date().toISOString(),
      appName: 'Selfi',
      recordingsCount: recordings.length,
      recordings: recordings
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selfi-backup-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (date: string | Date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (hours < 48) return 'Yesterday';
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="flex items-center justify-between p-4">
          <div>
            <h1 className="text-lg font-semibold">My Recordings</h1>
            <div className="flex items-center gap-2 mt-1">
              <Shield className="w-3 h-3 text-green-500" />
              <span className="text-xs text-gray-400">Private & stored on your device</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportAllRecordings}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title="Export all recordings"
            >
              <Archive className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/record')}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search recordings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 focus:border-purple-400 focus:outline-none"
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <Filter className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="px-4 pb-3 flex gap-2 overflow-x-auto">
            {['all', 'favorites', 'recent'].map(filter => (
              <button
                key={filter}
                onClick={() => setFilterCategory(filter)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filterCategory === filter
                    ? 'bg-purple-500 text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recordings List */}
      <div className="p-4 space-y-4 pb-4">
        {filteredRecordings.length === 0 ? (
          <div className="text-center py-12">
            <Volume2 className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-xl font-semibold mb-2">No recordings yet</h3>
            <p className="text-gray-400 mb-6">Start recording to build your motivation library</p>
            <button
              onClick={() => navigate('/record')}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
            >
              Start Recording
            </button>
          </div>
        ) : (
          filteredRecordings.map(recording => (
            <div
              key={recording.id}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 space-y-3"
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-400">
                      {formatDate(recording.createdAt)}
                    </span>
                    {recording.duration && (
                      <span className="text-xs text-gray-500">
                        • {Math.floor(recording.duration / 60)}:{(recording.duration % 60).toString().padStart(2, '0')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 line-clamp-1">{recording.prompt}</p>
                </div>
                <button
                  onClick={() => toggleFavorite(recording.id)}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Heart className={`w-5 h-5 ${recording.isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                </button>
              </div>

              {/* Response */}
              {editingId === recording.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    className="w-full p-3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 focus:border-purple-400 focus:outline-none resize-none"
                    rows={4}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(recording.id)}
                      className="px-4 py-2 bg-green-600 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-4 py-2 bg-gray-600 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm leading-relaxed line-clamp-3">{recording.response}</p>
              )}

              {/* Rating */}
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    onClick={() => updateRating(recording.id, star)}
                    className="p-0.5"
                  >
                    <Star
                      className={`w-4 h-4 ${
                        (recording.rating || 0) >= star
                          ? 'fill-yellow-500 text-yellow-500'
                          : 'text-gray-600'
                      }`}
                    />
                  </button>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                <button
                  onClick={() => togglePlayback(recording)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-purple-500/20 rounded-lg hover:bg-purple-500/30 transition-colors"
                >
                  {playingId === recording.id ? (
                    <>
                      <Pause className="w-4 h-4" />
                      <span className="text-sm">Pause</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span className="text-sm">Play</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => startEditing(recording)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => shareRecording(recording)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Share"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => downloadRecording(recording)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => deleteRecording(recording.id)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors text-red-400"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );
};